import { getLibraryFolders, LibraryTab } from "@/api";
import { DocumentDropZone } from "@/components/frontend/DocumentDropZone";
import { DocumentDetailLayout } from "@/components/frontend/documents/DocumentDetailLayout";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMediaQuery } from "@react-hookz/web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Navigate,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router";
import { toast } from "sonner";

import { CreateFolderDialog } from "./components/CreateFolderDialog";
import { DocumentList } from "./components/DocumentList";
import { FilterBar } from "./components/FilterBar";
import { TemplatesTab } from "./components/templates/TemplatesTab";
import { UploadDialog } from "./components/UploadDialog";
import { isForeignSharedSubject } from "./utils/folderTreeUtils";
import {
    isValidTypeFilter,
    LibraryTypeFilter,
    parseTypeFilter,
} from "./utils/libraryItemTypes";
import { LIBRARY_TAB_ROUTES } from "./utils/libraryTabRoutes";
import { SHARED_FOLDER_READ_ONLY_REASON } from "./utils/sharedKnowledgeBaseCopy";

const FOLDER_LOADING_REASON = "This folder is still loading.";
const FOLDER_UNAVAILABLE_REASON = "This folder isn't available.";

const TYPE_FILTER_TITLES: Record<LibraryTypeFilter, string> = {
    all: "Document Library",
    comparisons: "Comparisons",
    documents: "Document Library",
    scopes: "Scopes",
};

export const DocumentLibraryContent = () => {
    // @react-hookz/web returns `boolean | undefined` on the first SSR pass;
    // coerce to false so we render the desktop layout by default (matches
    // the prior MUI useMediaQuery behavior, which also returned false during
    // the first paint when no window matches were resolved yet).
    const isMobile = useMediaQuery("(max-width: 768px)") ?? false;
    const { folderId: folderIdParam } = useParams<{ folderId: string }>();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { settings } = useOrganization();
    const scopesEnabled = useScopesEnabled();

    // Derive active tab from URL path
    const activeTab: LibraryTab = location.pathname.includes("templates")
        ? "templates"
        : location.pathname.includes("knowledge-base")
          ? "knowledge_base"
          : "documents";

    const folderId = folderIdParam ? Number(folderIdParam) : null;

    const [searchParams, setSearchParams] = useSearchParams();

    const [searchValue, setSearchValue] = useState("");
    const [selectedTypeFilter, setSelectedTypeFilter] =
        useState<LibraryTypeFilter>(
            () =>
                parseTypeFilter(
                    searchParams.get("type_filter"),
                    scopesEnabled
                ) ?? "all"
        );
    const typeFilter =
        parseTypeFilter(selectedTypeFilter, scopesEnabled) ?? "all";
    if (typeFilter !== selectedTypeFilter) {
        setSelectedTypeFilter(typeFilter);
    }

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    // Files dropped on the page-level zone; passed to UploadDialog once per open.
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
        null
    );

    useDocumentTitle(
        activeTab === "knowledge_base"
            ? "Knowledge Base"
            : activeTab === "templates"
              ? "Templates"
              : TYPE_FILTER_TITLES[typeFilter]
    );

    const handleDocumentSelect = useCallback((id?: number) => {
        if (!id) {
            setSelectedDocumentId(null);
            return;
        }

        setSelectedDocumentId((prev) => (prev === id ? null : id));
    }, []);

    // Sync typeFilter when URL search params change (e.g. sidenav navigation)
    useEffect(() => {
        const param = searchParams.get("type_filter");
        if (param === null) return;
        const parsed = parseTypeFilter(param, scopesEnabled);
        if (parsed !== null) {
            setSelectedTypeFilter(parsed);
        } else if (isValidTypeFilter(param)) {
            // A real filter the org may no longer see: reset and drop the
            // param, which also restores the Document Library nav highlight
            // (route-matching treats any non-documents type_filter as a
            // competing view). A malformed value is left alone.
            setSelectedTypeFilter("all");
            searchParams.delete("type_filter");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, scopesEnabled]);
    // KB tab has no type filter dropdown: reset to "all" to avoid stale filter.
    // Close the detail panel on a genuine tab/filter transition away from
    // documents. The prev-value guard skips the initial mount (and StrictMode's
    // double-invoke), where this clear is a no-op today but would otherwise wipe
    // a `?doc=` deep-link seed.
    const prevTabFilterRef = useRef<{
        activeTab: LibraryTab;
        typeFilter: LibraryTypeFilter;
    } | null>(null);
    useEffect(() => {
        if (activeTab === "knowledge_base" && typeFilter !== "all") {
            setSelectedTypeFilter("all");
        }

        const prev = prevTabFilterRef.current;
        prevTabFilterRef.current = { activeTab, typeFilter };
        const transitioned =
            prev !== null &&
            (prev.activeTab !== activeTab || prev.typeFilter !== typeFilter);
        if (transitioned && typeFilter !== "documents") {
            handleDocumentSelect();
        }
    }, [activeTab, typeFilter, handleDocumentSelect]);

    // Seed-and-clear `?doc=` deep-link: on mount, open the detail panel for a
    // valid document id and drop the param so a stale URL can't re-trigger it.
    // Ref-guarded to run once (also under StrictMode's double-invoke). The
    // route's `:folderId` segment scopes the folder, so this stays
    // folder-agnostic; the panel self-fetches, so the doc need not be on the
    // current list page.
    const docSeededRef = useRef(false);
    useEffect(() => {
        if (docSeededRef.current) return;
        docSeededRef.current = true;
        const raw = searchParams.get("doc");
        if (raw === null) return;
        const id = Number(raw);
        if (!Number.isInteger(id) || id <= 0) return;
        setSelectedDocumentId(id);
        searchParams.delete("doc");
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const {
        data: foldersData,
        isPending: isFoldersPending,
        refetch: refetchFolders,
    } = useQuery({
        queryKey: ["frontend-library-folders", { includeShared: true }],
        queryFn: () => getLibraryFolders({ includeShared: true }),
        enabled: activeTab !== "templates",
    });

    const folders = useMemo(() => foldersData?.folders ?? [], [foldersData]);
    const ownFolders = useMemo(
        () => folders.filter((f) => !isForeignSharedSubject(f)),
        [folders]
    );
    const currentFolder = folders.find((f) => f.id === folderId) ?? null;
    const kbFolder = folders.find((f) => f.is_knowledge_base && !f.parent_id);
    const isForeignFolder =
        currentFolder !== null && isForeignSharedSubject(currentFolder);
    const isWriteTargetUnknown =
        folderId !== null
            ? currentFolder === null
            : activeTab === "knowledge_base" && kbFolder === undefined;
    const unresolvedFolderReason = isWriteTargetUnknown
        ? isFoldersPending
            ? FOLDER_LOADING_REASON
            : FOLDER_UNAVAILABLE_REASON
        : null;
    const writeUnavailableReason = isForeignFolder
        ? SHARED_FOLDER_READ_ONLY_REASON
        : unresolvedFolderReason;
    const previousDialogLocationRef = useRef({ activeTab, folderId });
    const dialogLocationChanged =
        previousDialogLocationRef.current.activeTab !== activeTab ||
        previousDialogLocationRef.current.folderId !== folderId;
    const dialogScopeKey = `${activeTab}:${folderId ?? "root"}:${writeUnavailableReason ? "blocked" : "writable"}`;

    useEffect(() => {
        previousDialogLocationRef.current = { activeTab, folderId };
        if (dialogLocationChanged || writeUnavailableReason) {
            setUploadDialogOpen(false);
            setCreateFolderDialogOpen(false);
            setDroppedFiles([]);
        }
    }, [activeTab, folderId, dialogLocationChanged, writeUnavailableReason]);

    // For upload dialog: when inside a folder, upload there.
    // At KB root, upload to first KB folder. At documents root, upload unfiled.
    const uploadFolderId =
        folderId ??
        (activeTab === "knowledge_base" ? (kbFolder?.id ?? null) : null);
    const uploadIsKB =
        currentFolder?.is_knowledge_base ??
        (activeTab === "knowledge_base" ? true : false);

    const handleUploadComplete = () => {
        refetchFolders();
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
    };

    const handleDocumentAction = () => {
        refetchFolders();
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
    };

    const handleFolderCreated = () => {
        setCreateFolderDialogOpen(false);
        refetchFolders();
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
    };

    // Copy varies by tab and whether the user is inside a named folder.
    const dropZoneTitle =
        writeUnavailableReason ??
        (currentFolder !== null
            ? `Upload files to ${currentFolder.name}`
            : activeTab === "knowledge_base"
              ? "Upload PDF or spreadsheet files to Knowledge Base"
              : "Upload PDF or spreadsheet files here");
    const dropZoneSubtitle = writeUnavailableReason
        ? "Files can't be added here."
        : activeTab === "documents" && currentFolder === null
          ? "We'll automatically match them to your existing clients"
          : "Drop PDF, Excel or CSV files here";

    const handleDropFiles = (files: File[]) => {
        if (writeUnavailableReason) {
            toast.error(writeUnavailableReason);
            return;
        }
        setDroppedFiles(files);
        setUploadDialogOpen(true);
    };

    if (activeTab === "templates" && !settings.output_templates_enabled) {
        return <Navigate to={LIBRARY_TAB_ROUTES.documents} replace />;
    }

    if (activeTab === "templates") {
        return <TemplatesTab />;
    }

    return (
        <>
            <DocumentDropZone
                title={dropZoneTitle}
                subtitle={dropZoneSubtitle}
                onDropFiles={handleDropFiles}
                disabled={uploadDialogOpen}
                // Every library surface (main + Knowledge Base) accepts
                // spreadsheets; only the scope/comparison pickers stay PDF-only.
                allowSpreadsheets
                // Bleed across DocumentLibraryLayout's pt/pb/pl-4 (mirrored
                // here) so the overlay reaches the pane edges, not inset;
                // flex-1 keeps the drop target filling the scrollport even
                // when the listing is short.
                className="-my-4 -ml-4 flex min-h-0 flex-1 flex-col py-4 pl-4"
            >
                <DocumentDetailLayout
                    isMobile={isMobile}
                    selectedDocumentId={selectedDocumentId}
                    onClose={() => setSelectedDocumentId(null)}
                >
                    {(panelResizeTick) => (
                        <>
                            <FilterBar
                                activeTab={activeTab}
                                showTemplatesTab={
                                    settings.output_templates_enabled
                                }
                                searchValue={searchValue}
                                onSearchChange={setSearchValue}
                                onSearchClear={() => setSearchValue("")}
                                typeFilter={typeFilter}
                                onTypeFilterChange={(
                                    value: LibraryTypeFilter
                                ) => {
                                    setSelectedTypeFilter(value);
                                    if (searchParams.has("type_filter")) {
                                        searchParams.delete("type_filter");
                                        setSearchParams(searchParams, {
                                            replace: true,
                                        });
                                    }
                                }}
                                onUploadClick={() => setUploadDialogOpen(true)}
                                onCreateFolderClick={() =>
                                    setCreateFolderDialogOpen(true)
                                }
                                currentFolder={currentFolder}
                                folders={folders}
                                newUnavailableReason={writeUnavailableReason}
                                onBreadcrumbClick={() => handleDocumentSelect()}
                                onDropComplete={handleDocumentAction}
                            />

                            <DocumentList
                                activeTab={activeTab}
                                folderId={folderId}
                                searchQuery={searchValue}
                                typeFilter={typeFilter}
                                onSearchClear={() => setSearchValue("")}
                                onDocumentAction={handleDocumentAction}
                                onUpload={
                                    writeUnavailableReason
                                        ? undefined
                                        : () => setUploadDialogOpen(true)
                                }
                                onDocumentSelect={handleDocumentSelect}
                                selectedDocumentId={selectedDocumentId}
                                panelResizeTick={panelResizeTick}
                                enableDragAndDrop={!isMobile}
                                folders={folders}
                                includeShared
                                clearSelectionOnResultSetChange
                                mutationsUnavailableReason={
                                    writeUnavailableReason
                                }
                            />
                        </>
                    )}
                </DocumentDetailLayout>
            </DocumentDropZone>

            <UploadDialog
                key={`upload:${dialogScopeKey}`}
                open={
                    uploadDialogOpen &&
                    !dialogLocationChanged &&
                    !writeUnavailableReason
                }
                onClose={() => {
                    setUploadDialogOpen(false);
                    setDroppedFiles([]);
                }}
                folderId={uploadFolderId}
                isKnowledgeBase={uploadIsKB}
                folders={ownFolders}
                onUploadComplete={handleUploadComplete}
                allowSpreadsheets
                initialFiles={
                    droppedFiles.length > 0 ? droppedFiles : undefined
                }
            />

            <CreateFolderDialog
                key={`create-folder:${dialogScopeKey}`}
                open={
                    createFolderDialogOpen &&
                    !dialogLocationChanged &&
                    !writeUnavailableReason
                }
                onClose={() => setCreateFolderDialogOpen(false)}
                onCreated={handleFolderCreated}
                parentId={
                    folderId ??
                    (activeTab === "knowledge_base"
                        ? (kbFolder?.id ?? null)
                        : null)
                }
                parentClient={
                    currentFolder?.client ??
                    (activeTab === "knowledge_base"
                        ? (kbFolder?.client ?? null)
                        : null)
                }
            />
        </>
    );
};
